import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiFetch } from '../../App';
import { ArrowLeft, CheckCircle2, Circle, Clock, MapPin, Scissors, Radio, AlertCircle, Loader2 } from 'lucide-react';
import LiveStreamViewer from '../../components/LiveStreamViewer';

const STATUS_STEPS = [
  { key: 'REQUESTED',  label: 'Booking Requested',  desc: 'Your request has been received' },
  { key: 'CONFIRMED',  label: 'Confirmed',           desc: 'Groomer is assigned & ready' },
  { key: 'IN_PROGRESS',label: 'In Progress',         desc: 'Grooming session is underway' },
  { key: 'COMPLETED',  label: 'Completed',           desc: 'Session finished successfully' },
];

const STATUS_ORDER = { REQUESTED: 0, CONFIRMED: 1, IN_PROGRESS: 2, COMPLETED: 3, CANCELLED: -1 };

export default function PawsBookingStatus() {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showViewer, setShowViewer] = useState(false);
  const [serviceLog, setServiceLog] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const b = await apiFetch(`/api/bookings/${id}`);
        setBooking(b);
        setLoading(false);
        if (b.status === 'COMPLETED') {
          try {
            const log = await apiFetch(`/api/service-logs/${id}`);
            if (log?.id) setServiceLog(log);
          } catch (e) {
            console.error('Failed to load service log:', e);
          }
        }
      } catch (e) {
        setError(e.message);
        setLoading(false);
      }
    };
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, [id]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <Loader2 size={36} className="animate-spin text-petfolk-orange" />
    </div>
  );

  if (error || !booking) return (
    <div className="max-w-2xl mx-auto pt-16 text-center">
      <AlertCircle size={48} className="mx-auto mb-4 text-red-400" />
      <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Booking not found</p>
      <Link to="/paws/bookings" className="btn-primary mt-4 inline-block">Back to Bookings</Link>
    </div>
  );

  const currentStep = STATUS_ORDER[booking.status] ?? 0;
  const isCancelled = booking.status === 'CANCELLED';

  return (
    <div className="max-w-2xl">
      <Link to="/paws/bookings" className="flex items-center gap-2 text-sm mb-6 hover:opacity-70 transition-opacity" style={{ color: 'var(--text-secondary)' }}>
        <ArrowLeft size={16} />
        Back to My Bookings
      </Link>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            {booking.service?.name}
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Booking ID: {booking.id.slice(0, 12)}...</p>
        </div>
        <span className={`badge badge-${booking.status.toLowerCase().replace('_', '-')} text-sm`}>{booking.status.replace('_', ' ')}</span>
      </div>

      {/* Status Timeline */}
      {!isCancelled && (
        <div className="petfolk-card p-6 mb-6">
          <h2 className="font-outfit font-bold mb-6" style={{ color: 'var(--text-primary)' }}>Booking Progress</h2>
          <div className="relative">
            {/* Connecting line */}
            <div className="absolute left-4 top-4 bottom-4 w-0.5" style={{ background: 'var(--border-color)' }} />
            <div
              className="absolute left-4 top-4 w-0.5 transition-all duration-700"
              style={{ background: 'var(--petfolk-orange)', height: `${Math.min(currentStep / (STATUS_STEPS.length - 1), 1) * 100}%` }}
            />
            <div className="space-y-6">
              {STATUS_STEPS.map((step, i) => {
                const done = i < currentStep;
                const active = i === currentStep;
                return (
                  <div key={step.key} className="flex items-start gap-4 relative">
                    <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all ${
                      done ? 'bg-petfolk-orange' : active ? 'bg-petfolk-orange/20 border-2 border-petfolk-orange' : ''
                    }`} style={!done && !active ? { background: 'var(--bg-secondary)', border: '2px solid var(--border-color)' } : {}}>
                      {done ? <CheckCircle2 size={16} color="white" /> : active ? <Clock size={14} className="text-petfolk-orange" /> : <Circle size={14} style={{ color: 'var(--border-color)' }} />}
                    </div>
                    <div className="pt-1">
                      <p className={`font-semibold text-sm ${active ? 'text-petfolk-orange' : done ? '' : ''}`}
                        style={{ color: active ? 'var(--petfolk-orange)' : done ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                        {step.label}
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>{step.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {isCancelled && (
        <div className="petfolk-card p-6 mb-6 border-l-4 border-red-400">
          <p className="font-semibold text-red-500">This booking has been cancelled</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Please reach out to support if you need assistance.</p>
        </div>
      )}

      {/* Live Stream Section */}
      {booking.liveStreamEnabled && (
        <div className="mb-6">
          {booking.liveStreamStatus === 'LIVE' && !showViewer && (
            <button
              onClick={() => setShowViewer(true)}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-bold text-white animate-pulse transition-all"
              style={{ background: 'linear-gradient(135deg, #EF4444, #8B5CF6)' }}
            >
              <Radio size={16} />
              🔴 Your groomer is LIVE — Watch Now!
            </button>
          )}
          {booking.liveStreamStatus === 'LIVE' && showViewer && (
            <LiveStreamViewer booking={booking} onClose={() => setShowViewer(false)} />
          )}
          {booking.liveStreamStatus === 'NOT_REQUESTED' && booking.status === 'IN_PROGRESS' && (
            <div className="flex items-center gap-3 p-4 rounded-2xl" style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.15)' }}>
              <Radio size={18} className="text-petfolk-purple" />
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Live Grooming Add-on</p>
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Your groomer will start the live session soon. This page will update automatically.</p>
              </div>
            </div>
          )}
          {booking.liveStreamStatus === 'ENDED' && (
            <div className="flex items-center gap-3 p-4 rounded-2xl" style={{ background: 'rgba(107,114,128,0.08)', border: '1px solid rgba(107,114,128,0.15)' }}>
              <Radio size={18} style={{ color: 'var(--text-secondary)' }} />
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Live session has ended</p>
            </div>
          )}
        </div>
      )}

      {/* Booking Details Card */}
      <div className="petfolk-card p-6 mb-6">
        <h2 className="font-outfit font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Session Details</h2>
        <div className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span style={{ color: 'var(--text-secondary)' }} className="flex items-center gap-2"><Scissors size={14} /> Service</span>
            <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{booking.service?.name}</span>
          </div>
          <div className="flex items-center justify-between">
            <span style={{ color: 'var(--text-secondary)' }} className="flex items-center gap-2">🐾 Pet</span>
            <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{booking.pet?.name} ({booking.pet?.species})</span>
          </div>
          <div className="flex items-center justify-between">
            <span style={{ color: 'var(--text-secondary)' }} className="flex items-center gap-2"><MapPin size={14} /> Location</span>
            <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{booking.node?.displayName}</span>
          </div>
          {booking.scheduledStart && (
            <div className="flex items-center justify-between">
              <span style={{ color: 'var(--text-secondary)' }} className="flex items-center gap-2"><Clock size={14} /> Scheduled</span>
              <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{new Date(booking.scheduledStart).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          )}
          <div className="flex items-center justify-between pt-3 mt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
            <span style={{ color: 'var(--text-secondary)' }}>Amount</span>
            <span className="font-outfit font-bold text-lg" style={{ color: 'var(--text-primary)' }}>₹{booking.netAmount?.toLocaleString()}</span>
          </div>
          <div className="flex items-center justify-between">
            <span style={{ color: 'var(--text-secondary)' }}>Payment</span>
            <span className={`badge badge-${booking.paymentStatus?.toLowerCase()}`}>{booking.paymentStatus}</span>
          </div>
        </div>
      </div>

      {/* Service Log (post-completion) */}
      {serviceLog && (
        <div className="petfolk-card p-6 mb-6">
          <h2 className="font-outfit font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Groomer's Report</h2>
          {serviceLog.groomingNotes && (
            <div className="mb-3">
              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>SESSION NOTES</p>
              <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{serviceLog.groomingNotes}</p>
            </div>
          )}
          {serviceLog.behaviorObservations && (
            <div className="mb-3">
              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>BEHAVIOR</p>
              <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{serviceLog.behaviorObservations}</p>
            </div>
          )}
          {serviceLog.nextGroomingRecommendation && (
            <div className="mb-3">
              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>NEXT GROOMING</p>
              <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{serviceLog.nextGroomingRecommendation}</p>
            </div>
          )}
          {serviceLog.productsUsed?.length > 0 && (
            <div>
              <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>PRODUCTS USED</p>
              <div className="flex flex-wrap gap-2">
                {serviceLog.productsUsed.map(p => (
                  <span key={p} className="badge" style={{ background: 'var(--bg-secondary)' }}>{p}</span>
                ))}
              </div>
            </div>
          )}
          {/* Before/After photos */}
          {(serviceLog.beforePhotoUrl || serviceLog.afterPhotoUrl) && (
            <div>
              <p className="text-xs font-semibold mb-2 mt-3" style={{ color: 'var(--text-secondary)' }}>BEFORE & AFTER</p>
              <div className="grid grid-cols-2 gap-3">
                {serviceLog.beforePhotoUrl && (
                  <div>
                    <p className="text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>Before</p>
                    <img src={serviceLog.beforePhotoUrl} alt="Before" className="w-full rounded-xl object-cover" style={{ aspectRatio: '4/3' }} />
                  </div>
                )}
                {serviceLog.afterPhotoUrl && (
                  <div>
                    <p className="text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>After</p>
                    <img src={serviceLog.afterPhotoUrl} alt="After" className="w-full rounded-xl object-cover" style={{ aspectRatio: '4/3' }} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
